/**
 * Custom Menu
 *
 * Standalone rail: solid Ink background (palette.background.menu),
 * white/translucent-white items, the brand text lockup at the TOP (the brand
 * name appears in exactly one place), a white 2px marker on the active item
 * and the profile card fixed at the bottom (replaces the app-bar user menu).
 * Responsive pattern unchanged: an independent `sm` breakpoint check renders
 * the panel at full width on mobile, and clicking an item auto-closes it.
 *
 * One entry per SECTION of the navigation model (`./navigation`), not one per
 * screen: the rail answers "what am I working on", and the app-bar switcher
 * moves between the screens of the section. Seven entries for an
 * administrator, fewer for every other role — short enough that group
 * headings would be noise, so there are none.
 */

import React from 'react';
import { useTranslate, useSidebarState, usePermissions } from 'react-admin';
import { Box, Tooltip, useMediaQuery, MenuItem as MuiMenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { ProfileRailCard } from './ProfileRailCard';
import { RailBrand } from '../components/common/BrandLockup';
import { getVisibleSections, railIconOf, railLabelOf, resolveRoute } from './navigation';

interface StyledMenuItemProps {
  open: boolean;
}

const StyledMenuItem = styled(MuiMenuItem, {
  shouldForwardProp: (prop) => prop !== 'open',
})<StyledMenuItemProps>(({ theme, open }) => ({
  position: 'relative',
  margin: theme.custom.menu.item.margin,
  padding: theme.custom.menu.item.padding,
  borderRadius: theme.custom.menu.item.borderRadius,
  color: theme.custom.menu.item.color(theme),
  justifyContent: open ? 'flex-start' : 'center',
  fontSize: theme.custom.menu.item.fontSize,
  '& .MuiListItemText-root': {
    minWidth: 0,
  },
  '& .MuiListItemText-primary': {
    fontSize: theme.custom.menu.item.fontSize,
    fontWeight: theme.custom.menu.item.fontWeight,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  display: 'flex',
  gap: theme.custom.menu.item.gap,
  transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',

  '& .MuiListItemIcon-root': {
    minWidth: 'auto',
    color: theme.custom.menu.item.iconColor(theme),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  '&:hover:not(.active)': {
    color: theme.custom.menu.item.hover.color(theme),
    backgroundColor: open ? theme.custom.menu.item.hover.backgroundColor(theme) : 'transparent',
    ...(!open && {
      '& .MuiListItemIcon-root': {
        backgroundColor: theme.custom.menu.item.hover.backgroundColor(theme),
      },
    }),
  },

  '&.active': {
    color: open ? theme.custom.menu.item.active.color(theme) : theme.palette.primary.main,
    backgroundColor: open ? theme.custom.menu.item.active.backgroundColor(theme) : 'transparent',
    fontWeight: theme.custom.menu.item.active.fontWeight,
    '& .MuiListItemText-primary': {
      fontWeight: theme.custom.menu.item.active.fontWeight,
    },

    '&::before': open
      ? {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 6,
          bottom: 6,
          width: 2,
          borderRadius: '0 2px 2px 0',
          backgroundColor: theme.custom.menu.item.active.marker(theme),
        }
      : undefined,

    '& .MuiListItemIcon-root': {
      color: open ? theme.custom.menu.item.active.marker(theme) : theme.custom.menu.item.closedActiveIcon.color(theme),
      backgroundColor: open ? 'transparent' : theme.custom.menu.item.closedActiveIcon.backgroundColor(theme),
      padding: open ? 0 : '6px',
      borderRadius: open ? 0 : (theme.custom.menu.item.closedActiveIcon.borderRadius || '6px'),
    },

    '&:hover': {
      backgroundColor: open ? theme.custom.menu.item.active.hover.backgroundColor(theme) : 'transparent',
    },
  },
}));

export const CustomMenu: React.FC = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setSidebarOpen] = useSidebarState();
  // usePermissions() is only used as a re-render signal once auth resolves;
  // the actual decision is role + plan-module based via canAccessFeature.
  usePermissions();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Recomputed on every render (a handful of synchronous localStorage reads),
  // so the rail follows a role or plan change without any cache to invalidate.
  const sections = getVisibleSections();

  const active = resolveRoute(location.pathname, sections);

  const handleMenuClick = (to: string): void => {
    navigate(to);
    if (isSmallScreen) {
      setSidebarOpen(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        // Pattern A: the rail spans the FULL viewport height, flush to the
        // top and bottom edges. The AppBar is absolutely positioned over the
        // content column only (see CustomAppBar/CustomLayout), so there is
        // no app-bar strip or outer gutter to subtract here.
        height: '100vh',
        boxSizing: 'border-box',
        backgroundColor: (theme.palette.background as any).menu,
        borderRight: isSmallScreen ? 'none' : `1px solid ${theme.custom.menu.border}`,
        padding: isSmallScreen ? '12px' : isSidebarOpen ? '16px 14px 16px 8px' : '16px 6px',
        width: isSmallScreen ? '94%' : isSidebarOpen ? theme.custom.menu.openWidth : theme.custom.menu.closedWidth,
        minWidth: isSmallScreen ? 'auto' : isSidebarOpen ? theme.custom.menu.openWidth : theme.custom.menu.closedWidth,
        maxWidth: isSmallScreen ? '100%' : isSidebarOpen ? theme.custom.menu.openWidth : theme.custom.menu.closedWidth,
        transition:
          'width 0.2s ease-in-out, min-width 0.2s ease-in-out, max-width 0.2s ease-in-out, padding 0.2s ease-in-out',
        overflow: 'hidden',
      }}
    >
      {(isSidebarOpen || isSmallScreen) && (
        <Box sx={{ display: 'flex', padding: '4px 8px 20px' }}>
          {/* The single place the brand name appears inside the app. */}
          <RailBrand />
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          overflowY: isSidebarOpen || isSmallScreen ? 'auto' : 'hidden',
          overflowX: 'hidden',
          // Rail-native scrollbar: thin + translucent white (the global cream
          // scrollbar reads wrong on the teal rail).
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255,255,255,0.22)',
            borderRadius: '999px',
          },
          '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(255,255,255,0.35)' },
        }}
      >
        {sections.map((section) => {
          const expanded = isSidebarOpen || isSmallScreen;
          const { labelKey, smartCount } = railLabelOf(section);
          const label = translate(labelKey, { smart_count: smartCount });
          // A collapsed section borrows its only item's role hint; a section
          // with several items has no single hint to show.
          const hintKey = section.items.length === 1 ? section.items[0].tooltipKey : undefined;
          const hint = hintKey ? translate(hintKey) : '';
          // Collapsed rail: the tooltip names the entry (icons alone are
          // ambiguous), appending the role hint when present. Expanded rail:
          // the label is already visible, so only the role hint is worth
          // surfacing (empty string → no tooltip).
          const tooltipTitle = expanded ? hint : [label, hint].filter(Boolean).join(' — ');
          return (
            <Tooltip key={section.key} title={tooltipTitle} placement="right" arrow disableInteractive>
              <StyledMenuItem
                onClick={() => handleMenuClick(section.items[0].to)}
                open={isSidebarOpen}
                className={active?.section.key === section.key ? 'active' : ''}
              >
                <ListItemIcon>{railIconOf(section)}</ListItemIcon>
                {expanded && <ListItemText primary={label} primaryTypographyProps={{ noWrap: true }} />}
              </StyledMenuItem>
            </Tooltip>
          );
        })}
      </Box>

      <ProfileRailCard collapsed={!(isSidebarOpen || isSmallScreen)} />
    </Box>
  );
};

export default CustomMenu;
